import { Form } from "antd";
import { ColumnsType } from "antd/es/table";
import {
  Result,
  SelectList,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { AuthenticationContext } from "context/AuthenticationContext";
import { GeneralsInjection } from "features/generals/di/GeneralsInjection";
import {
  TransactionDetailReportItem,
  TransactionDetailReportResponseEntity,
} from "features/generals/domain/entities/TransactionDetailReportResponseEntity";
import {
  TransactionReportItem,
  TransactionReportResponseEntity,
} from "features/generals/domain/entities/TransactionReportResponseEntity";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { productCache } from "utils/cache/productCache";
import { ServiceEnum } from "utils/enums/ServiceEnum";
import { NumberFormat } from "utils/format/NumberFormat";
import { formatCurrency, getMessage } from "utils/helpersInTs/helpersInTs";
import {
  getPartnerOfUserFromCache,
  isLsgdChainOrStoreUser,
} from "utils/helpersInTs/PartnerRoleForTrans";
import { strings } from "utils/localizedStrings";
import { GeneralConstants } from "../constants/GeneralConstants";
import {
  GeneralRangeType,
  GeneralRowType,
  GeneralStatCards,
} from "../interfaces/IGenerals";

const GeneralViewModel = () => {
  const [loading, setLoading] = useState(false);
  const [dataForCharts, setDataForCharts] = useState([]);
  const [revenue, setRevenue] = useState({});
  const [resultObject, setResultObject] = useState<Result | null>(null);
  const [transactionReportData, setTransactionReportData] = useState<
    Array<TransactionReportItem>
  >([]);
  const [transactionReportResponse, setTransactionReportResponse] = useState<
    Array<TransactionReportResponseEntity>
  >([]);
  const [partnerList, setPartnerList] = useState<
    Array<BaseFilterListResponseModel>
  >([]);
  const [carrierList, setCarrierList] = useState<
    Array<BaseFilterListResponseModel>
  >([]);
  const [transactionReportLoading, setTransactionReportLoading] =
    useState(false);
  const [transactionDetailReportData, setTransactionDetailReportData] =
    useState<TransactionDetailReportItem[]>([]);
  const [transactionDetailReportResponse, setTransactionDetailReportResponse] =
    useState<Array<TransactionDetailReportResponseEntity>>([]);
  const [transactionDetailReportLoading, setTransactionDetailReportLoading] =
    useState(false);

  const [service, setService] = useState<string>("ALL");
  const [range, setRange] = useState<GeneralRangeType>("day");
  const [currency, setCurrency] = useState<string>("VND");
  const [currencyOptions, setCurrencyOptions] = useState<Array<SelectList>>([
    { label: "VND", value: "VND" },
  ]);
  const [reportCurrencies, setReportCurrencies] = useState<string[]>([]);
  const [detailCurrencies, setDetailCurrencies] = useState<string[]>([]);
  const [selectedPartnerType, setSelectedPartnerType] = useState<number>(0);
  const [selectedPartnerCode, setSelectedPartnerCode] = useState<string>("");
  const [selectedProviderCode, setSelectedProviderCode] = useState<string>("");
  const [isStore, setIsStore] = useState(false);

  // Store and staff state management
  const [storeOptions, setStoreOptions] = useState<Array<SelectList>>([]);
  const [staffOptions, setStaffOptions] = useState<Array<SelectList>>([]);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [selectedStaffs, setSelectedStaffs] = useState<string[]>([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [filterForm] = Form?.useForm();
  const useCase = GeneralsInjection.getUseCase();
  const transactionReportLoadingVersionRef = useRef(0);
  const transactionDetailLoadingVersionRef = useRef(0);

  const { config, user, serviceOptions, language } = AuthenticationContext();
  const {
    defaultStatCards,
    extractCurrencies,
    formatChangeRate,
    formatPeriodLabel,
    getCurrencyEnum,
    getDisplayStatByCurrency,
  } = GeneralConstants;

  const buildRowData = (
    items: TransactionDetailReportItem[],
    periodLabels: string[],
    range: GeneralRangeType,
    valueField: "quantity" | "revenue",
  ): GeneralRowType[] => {
    return items.map((item, index) => {
      const statMap: Record<string, number> = (item.stat || []).reduce(
        (acc, s: any) => {
          const key = formatPeriodLabel(String(s.time), range);
          acc[key] = Number(s?.[valueField] || 0);
          return acc;
        },
        {} as Record<string, number>,
      );

      const totalRevenue = (item.stat || []).reduce(
        (sum, s: any) => sum + Number(s.revenue || 0),
        0,
      );
      const totalTransactions = (item.stat || []).reduce(
        (sum, s: any) => sum + Number(s.quantity || 0),
        0,
      );

      return {
        key: index.toString(),
        name: item.name || item.service || `Item ${index + 1}`,
        service: item.service,
        carrier: item.carrier,
        ...periodLabels.reduce(
          (acc, label) => ({
            ...acc,
            [label]: statMap[label] || 0,
          }),
          {} as Record<string, number>,
        ),
        totalRevenue,
        totalTransactions,
      };
    });
  };

  const buildColumns = (
    periodLabels: string[],
    currencyCode: string,
    valueType: "currency" | "number" = "currency",
  ): ColumnsType<GeneralRowType> => {
    const dynamicCols: ColumnsType<GeneralRowType> = periodLabels.map(
      (label) => ({
        title: label,
        dataIndex: label,
        align: "right",
        width: 200,
        render: (val: any) =>
          valueType === "currency"
            ? formatCurrency(
                Number(val || 0),
                true,
                true,
                getCurrencyEnum(currencyCode),
              )
            : Number(val || 0).toLocaleString(),
      }),
    );

    return [
      {
        title: "Danh mục",
        dataIndex: "name",
        fixed: "left",
        width: 150,
      },
      ...dynamicCols,
    ];
  };

  const syncCurrencyOptions = (
    nextReportCurrencies: string[] = reportCurrencies,
    nextDetailCurrencies: string[] = detailCurrencies,
  ) => {
    const availableCurrencies = Array.from(
      new Set(
        [...nextReportCurrencies, ...nextDetailCurrencies]
          .map((item) =>
            String(item || "")
              .toUpperCase()
              .trim(),
          )
          .filter(Boolean),
      ),
    );

    const finalCurrencies =
      availableCurrencies.length > 0 ? availableCurrencies : ["VND"];

    setCurrencyOptions(
      finalCurrencies.map((item) => ({
        label: item,
        value: item,
      })),
    );

    const nextCurrency = finalCurrencies.includes(currency)
      ? currency
      : finalCurrencies.includes("VND")
        ? "VND"
        : finalCurrencies[0];

    setCurrency(nextCurrency);
    filterForm?.setFieldValue("currency", nextCurrency);
  };

  const buildStatCards = (
    transactionReportData: TransactionReportItem[],
    currencyCode: string,
  ) => {
    if (!transactionReportData || transactionReportData.length === 0) {
      return defaultStatCards(currencyCode);
    }

    const today = transactionReportData?.[0];
    const thisMonth = transactionReportData?.[1];
    const previousMonth = transactionReportData?.[2];

    return {
      todayRevenue: {
        currentTime: today?.currentTime ?? strings.General.Revenue.CurrentDate,
        previousTime:
          today?.previousTime ?? strings.General.Revenue.PreviousDate,
        value: today
          ? formatCurrency(
              today.revenue ?? 0,
              true,
              true,
              getCurrencyEnum(currencyCode),
            )
          : formatCurrency(0, true, true, getCurrencyEnum(currencyCode)),
        change:
          today?.revenueChangeRate !== undefined &&
          today?.revenueChangeRate !== null
            ? formatChangeRate(today.revenueChangeRate)
            : "",
        trend: (today?.revenueChangeRate || 0) >= 0 ? "up" : "down",
      },
      monthRevenue: {
        currentTime:
          thisMonth?.currentTime ?? strings.General.Revenue.CurrentMonth,
        previousTime:
          thisMonth?.previousTime ?? strings.General.Revenue.LastMonth,
        value: thisMonth
          ? formatCurrency(
              thisMonth.revenue ?? 0,
              true,
              true,
              getCurrencyEnum(currencyCode),
            )
          : formatCurrency(0, true, true, getCurrencyEnum(currencyCode)),
        change:
          thisMonth?.revenueChangeRate !== undefined &&
          thisMonth?.revenueChangeRate !== null
            ? formatChangeRate(thisMonth.revenueChangeRate)
            : "",
        trend: (thisMonth?.revenueChangeRate || 0) >= 0 ? "up" : "down",
      },
      previousMonthRevenue: {
        currentTime:
          previousMonth?.currentTime ?? strings.General.Revenue.LastMonth,
        previousTime:
          previousMonth?.previousTime ??
          strings.General.Revenue.LastMonthBefore,
        value: previousMonth
          ? formatCurrency(
              previousMonth.revenue ?? 0,
              true,
              true,
              getCurrencyEnum(currencyCode),
            )
          : formatCurrency(0, true, true, getCurrencyEnum(currencyCode)),
        change:
          previousMonth?.revenueChangeRate !== undefined &&
          previousMonth?.revenueChangeRate !== null
            ? formatChangeRate(previousMonth.revenueChangeRate)
            : "",
        trend: (previousMonth?.revenueChangeRate || 0) >= 0 ? "up" : "down",
      },
      todayTransactions: {
        currentTime: today?.currentTime ?? strings.General.Revenue.CurrentDate,
        previousTime:
          today?.previousTime ?? strings.General.Revenue.PreviousDate,
        value: today
          ? NumberFormat.currencyFormat(today.quantity, false)
          : NumberFormat.currencyFormat(0, false),
        change:
          today?.quantityChangeRate !== undefined &&
          today?.quantityChangeRate !== null
            ? formatChangeRate(today.quantityChangeRate)
            : "",
        trend: (today?.quantityChangeRate || 0) >= 0 ? "up" : "down",
      },
      monthTransactions: {
        currentTime:
          thisMonth?.currentTime ?? strings.General.Revenue.CurrentMonth,
        previousTime:
          thisMonth?.previousTime ?? strings.General.Revenue.LastMonth,
        value: thisMonth
          ? NumberFormat.currencyFormat(thisMonth.quantity, false)
          : NumberFormat.currencyFormat(0, false),
        change:
          thisMonth?.quantityChangeRate !== undefined &&
          thisMonth?.quantityChangeRate !== null
            ? formatChangeRate(thisMonth.quantityChangeRate)
            : "",
        trend: (thisMonth?.quantityChangeRate || 0) >= 0 ? "up" : "down",
      },
      previousMonthTransactions: {
        currentTime:
          previousMonth?.currentTime ?? strings.General.Revenue.LastMonth,
        previousTime:
          previousMonth?.previousTime ??
          strings.General.Revenue.LastMonthBefore,
        value: previousMonth
          ? NumberFormat.currencyFormat(previousMonth.quantity, false)
          : NumberFormat.currencyFormat(0, false),
        change:
          previousMonth?.quantityChangeRate !== undefined &&
          previousMonth?.quantityChangeRate !== null
            ? formatChangeRate(previousMonth.quantityChangeRate)
            : "",
        trend: (previousMonth?.quantityChangeRate || 0) >= 0 ? "up" : "down",
      },
    } as GeneralStatCards;
  };

  const getPartner = async () => {
    try {
      const res = await useCase.getPartnerList({
        page: 0,
        limit: 500,
        status: 0,
      });
      if (res) {
        setPartnerList(res);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getCarrier = async () => {
    try {
      const res = await useCase.getCarrierList({
        page: 0,
        limit: 100,
      });
      if (res) {
        setCarrierList(res);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const loadStoreOptions = async (params: {
    providerCode?: string;
    partnerCode: string;
  }) => {
    try {
      const res = await useCase.getStoreList(params);
      if (res) {
        setStoreOptions([
          { label: strings.GlobalLabels.All, value: "" },
          ...(res || []).map((item) => ({
            label: item?.store_name,
            value: item?.store_code,
          })),
        ]);
      }
    } catch (error) {
      console.error(error);
      setStoreOptions([{ label: strings.GlobalLabels.All, value: "" }]);
    }
  };

  const loadStaffOptions = async (params: {
    providerCode?: string;
    partnerCode: string;
    storeCode: string;
  }) => {
    try {
      setStaffLoading(true);
      const res = await useCase.getStaffList(params);
      if (res) {
        const raw = res ?? [];
        const allUsers = raw.flatMap((item: any) => item?.users ?? []);
        const mappedUsers: SelectList[] = allUsers.map((u: any) => ({
          label: u.name || u.userName || u.email || u.phone || "",
          value: u.id,
          type: u.type,
        }));
        setStaffOptions([
          { label: strings.GlobalLabels.All, value: "" },
          ...mappedUsers,
        ]);
      }
    } catch (error) {
      console.error(error);
      setStaffOptions([{ label: strings.GlobalLabels.All, value: "" }]);
    } finally {
      setStaffLoading(false);
    }
  };

  const handlePartnerChange = async (partnerValue: string) => {
    const selectedPartner = partnerList.find(
      (p: any) =>
        (p?.partnerRefCode && p?.partnerRefCode === partnerValue) ||
        p?.code === partnerValue,
    ) as any;
    const type = selectedPartner?.type ?? 0;
    const providerCode = selectedPartner?.providerCode || "";
    const isStorePartner = !!selectedPartner?.isStore;

    setSelectedPartnerCode(selectedPartner?.code || "");
    setSelectedPartnerType(type);
    setSelectedProviderCode(providerCode);
    setIsStore(isStorePartner);
    setSelectedStores([]);
    setSelectedStaffs([]);
    setStoreOptions([{ label: strings.GlobalLabels.All, value: "" }]);
    setStaffOptions([{ label: strings.GlobalLabels.All, value: "" }]);

    filterForm?.resetFields(["store", "staff"]);

    if (type === 1 && isStorePartner && selectedPartner?.code) {
      await loadStoreOptions({
        providerCode,
        partnerCode: selectedPartner.code,
      });
    }
  };

  const handleStoreChange = async (storeValue: string) => {
    setSelectedStores(storeValue ? [storeValue] : []);
    setSelectedStaffs([]);
    filterForm?.setFieldValue("staff", [""]);
    setStaffOptions([{ label: strings.GlobalLabels.All, value: "" }]);

    if (!storeValue || !selectedPartnerCode) return;

    await loadStaffOptions({
      providerCode: selectedProviderCode,
      partnerCode: selectedPartnerCode,
      storeCode: storeValue,
    });
  };

  const handleStaffChange = (staffValues: string[]) => {
    const cleanValues = (staffValues || []).filter(Boolean);
    setSelectedStaffs(cleanValues);

    if (cleanValues.length > 0) {
      filterForm?.setFieldValue("staff", cleanValues);
    } else {
      filterForm?.setFieldValue("staff", [""]);
    }
  };

  const syncPartnerRoleFilters = async () => {
    const partner = getPartnerOfUserFromCache()?.[0] as any;
    if (!isLsgdChainOrStoreUser(user) || !partner?.code) return;

    const partnerValue = partner?.partnerRefCode || partner?.code;
    const storeCode = partner?.store_code || partner?.storeCode;

    filterForm?.setFieldValue("partner", partnerValue);
    if (storeCode) {
      filterForm?.setFieldValue("store", storeCode);
      setSelectedStores([storeCode]);
    }

    setSelectedPartnerCode(partner.code);
    setSelectedPartnerType(1);
    setSelectedProviderCode(partner.providerCode || "");
    setIsStore(true);
    setStoreOptions(
      storeCode
        ? [
            { label: strings.GlobalLabels.All, value: "" },
            {
              label: partner?.store_name || partner?.storeName || storeCode,
              value: storeCode,
            },
          ]
        : [{ label: strings.GlobalLabels.All, value: "" }],
    );
    setStaffOptions([{ label: strings.GlobalLabels.All, value: "" }]);
    setSelectedStaffs([]);

    await loadStoreOptions({
      providerCode: partner.providerCode,
      partnerCode: partner.code,
    });

    if (storeCode) {
      await loadStaffOptions({
        providerCode: partner.providerCode,
        partnerCode: partner.code,
        storeCode,
      });
    }
  };

  const getDataForReports = async () => {
    try {
      setLoading(true);
      const res = await useCase.getGeneralData();
      if (res?.data) {
        const temp = res?.data?.map((item) => {
          const time = moment(item?.time, "MM-YYYY").format("MM-YYYY");
          return {
            time,
            value: item?.total,
            count: item?.count,
          };
        });
        setDataForCharts(temp);
      } else {
        setResultObject({
          type: "error",
          message: res?.message || strings.GlobalMessage.Error,
        });
      }
    } catch (error) {
      console.error(error);
      setResultObject({ type: "error", message: strings.GlobalMessage.Error });
    } finally {
      setLoading(false);
    }
  };

  const getTransactionReport = async (serviceCode: string) => {
    const requestVersion = ++transactionReportLoadingVersionRef.current;
    try {
      const formVal = filterForm?.getFieldsValue(true);
      setTransactionReportLoading(true);
      const params = useCase.buildTransactionReportParams({
        serviceCode,
        selectedStores,
        selectedStaffs,
        staffOptions: mappedStaffOptionsForUseCase,
        telco: formVal?.telco,
        partnerRefCode: formVal?.partner,
        partnerList,
      });

      const res = await useCase.getTransactionReport(params);
      if (!res) return;
      if (res?.data) {
        setTransactionReportResponse(res.data);
        const nextCurrencies = extractCurrencies(res.data);
        syncCurrencyOptions(nextCurrencies, detailCurrencies);
        setReportCurrencies(nextCurrencies);
      } else {
        setTransactionReportResponse([]);
        setTransactionReportData([]);
        setReportCurrencies([]);
        syncCurrencyOptions([], detailCurrencies);
        setResultObject({
          type: "error",
          message: res?.message || strings.GlobalMessage.Error,
        });
      }
    } catch (error) {
      console.error(error);
      getMessage(error?.message, 3, "error");
    } finally {
      if (requestVersion === transactionReportLoadingVersionRef.current) {
        setTransactionReportLoading(false);
      }
    }
  };

  const getTransactionDetailReport = async (
    serviceCode: string,
    reportTypeValue: number,
  ) => {
    const requestVersion = ++transactionDetailLoadingVersionRef.current;
    try {
      if (serviceCode !== "ALL") {
        setTransactionDetailReportLoading(true);
        const formVal = filterForm?.getFieldsValue(true);
        const params = useCase.buildTransactionDetailReportParams({
          serviceCode,
          reportType: reportTypeValue,
          selectedStores,
          selectedStaffs,
          staffOptions: mappedStaffOptionsForUseCase,
          telco: formVal?.telco,
          partnerRefCode: formVal?.partner,
          partnerList,
        });

        const res = await useCase.getTransactionDetailReport(params);
        if (!res) return;
        if (res?.data) {
          setTransactionDetailReportResponse(res.data);
        } else {
          setTransactionDetailReportResponse([]);
          setTransactionDetailReportData([]);
          setDetailCurrencies([]);
          setResultObject({
            type: "error",
            message: res?.message || strings.GlobalMessage.Error,
          });
        }
      }
    } catch (error) {
      console.error(error);
      getMessage(error?.message, 3, "error");
    } finally {
      if (
        requestVersion === transactionDetailLoadingVersionRef.current
      ) {
        setTransactionDetailReportLoading(false);
      }
    }
  };

  const exportTransactionDetailReport = async (
    serviceCode: string,
    reportTypeValue: number,
    extras?: { store?: string; staff?: string },
  ) => {
    const formVal = filterForm?.getFieldsValue(true);
    const params = useCase.buildExportTransactionDetailReportParams({
      serviceCode,
      reportType: reportTypeValue,
      extras,
      staffOptions: mappedStaffOptionsForUseCase,
      telco: formVal?.telco,
      partnerRefCode: formVal?.partner,
      partnerList,
    });

    const res = await useCase.exportTransactionDetailReport(params);
    return res?.data as unknown as Blob;
  };

  const reportType = useMemo(
    () => (range === "day" ? 0 : range === "week" ? 1 : 2),
    [range],
  );

  const mappedTransactionReportData = useMemo(
    () =>
      getDisplayStatByCurrency<TransactionReportItem>(
        transactionReportResponse,
        currency,
      ),
    [transactionReportResponse, currency],
  );

  const mappedTransactionDetailReportData = useMemo(
    () =>
      getDisplayStatByCurrency<TransactionDetailReportItem>(
        transactionDetailReportResponse,
        currency,
      ),
    [transactionDetailReportResponse, currency],
  );

  const processedStatCards = useMemo(
    () => buildStatCards(mappedTransactionReportData, currency),
    [mappedTransactionReportData, currency],
  );

  const periodLabels = useMemo(() => {
    const first = mappedTransactionDetailReportData?.[0];
    const stats = first?.stat || [];
    return stats.map((s: any) => formatPeriodLabel(String(s.time), range));
  }, [mappedTransactionDetailReportData, range]);

  const processedTableData = useMemo(() => {
    if (!mappedTransactionDetailReportData?.length) {
      return [];
    }
    return buildRowData(
      mappedTransactionDetailReportData,
      periodLabels,
      range,
      "quantity",
    );
  }, [mappedTransactionDetailReportData, periodLabels, range]);

  const processedRevenueData = useMemo(() => {
    if (!mappedTransactionDetailReportData?.length) {
      return [];
    }
    return buildRowData(
      mappedTransactionDetailReportData,
      periodLabels,
      range,
      "revenue",
    );
  }, [mappedTransactionDetailReportData, periodLabels, range]);

  const revenueColumns = useMemo(
    () => buildColumns(periodLabels, currency, "currency"),
    [periodLabels, currency],
  );
  const volumeColumns = useMemo(
    () => buildColumns(periodLabels, currency, "number"),
    [periodLabels, currency],
  );

  const mappedStaffOptionsForUseCase = useMemo(
    () =>
      staffOptions.map((item) => ({
        value: String(item?.value || ""),
        type: item?.type,
      })),
    [staffOptions],
  );

  const refreshReports = () => {
    getTransactionReport(service);
    getTransactionDetailReport(service, reportType);
  };

  const resetFilters = () => {
    filterForm?.resetFields(["partner", "telco", "currency", "store", "staff"]);
    const resetCurrency = currencyOptions.some((item) => item?.value === "VND")
      ? "VND"
      : String(currencyOptions?.[0]?.value || "VND");
    filterForm?.setFieldValue("currency", resetCurrency);
    setCurrency(resetCurrency);
    setSelectedStores([]);
    setSelectedStaffs([]);
    setStoreOptions([{ label: strings.GlobalLabels.All, value: "" }]);
    setStaffOptions([{ label: strings.GlobalLabels.All, value: "" }]);
    setSelectedPartnerType(0);
    setSelectedPartnerCode("");
    setSelectedProviderCode("");
    setIsStore(false);
    void syncPartnerRoleFilters();
    refreshReports();
  };

  const onFilterValuesChange = () => {
    const formVal = filterForm?.getFieldsValue(true);
    setCurrency(formVal?.currency || "VND");
  };

  const onGeneralFilterValuesChange = (
    changedValues: Record<string, unknown>,
  ) => {
    const formVal = filterForm?.getFieldsValue(true);
    setCurrency(formVal?.currency || "VND");

    if (Object.prototype.hasOwnProperty.call(changedValues, "currency")) {
      return;
    }

    if (
      Object.prototype.hasOwnProperty.call(changedValues, "store") ||
      Object.prototype.hasOwnProperty.call(changedValues, "staff")
    ) {
      return;
    }

    refreshReports();
  };

  const handleExport = async () => {
    const blob = await exportTransactionDetailReport(service, reportType, {
      store: selectedStores.filter(Boolean) as any,
      staff: selectedStaffs.filter(Boolean) as any,
    });
    if (!blob) return;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `dashboard_${service}_${range}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    filterForm?.setFieldValue("currency", "VND");
    getPartner();
    getCarrier();
  }, []);

  useEffect(() => {
    const allLabel = strings.GlobalLabels.All;

    setStoreOptions((prev) => {
      if (!prev?.length) return prev;
      const [, ...rest] = prev;
      return [{ label: allLabel, value: "" }, ...rest];
    });

    setStaffOptions((prev) => {
      if (!prev?.length) return prev;
      const [, ...rest] = prev;
      return [{ label: allLabel, value: "" }, ...rest];
    });
  }, [language]);

  useEffect(() => {
    if (!selectedPartnerCode) {
      void syncPartnerRoleFilters();
    }
  }, [user]);

  useEffect(() => {
    getTransactionReport(service);
  }, [service, selectedStores, selectedStaffs]);

  useEffect(() => {
    getTransactionDetailReport(service, reportType);
  }, [service, selectedStores, selectedStaffs, reportType]);

  const finalFilterOptions = useMemo(() => {
    return [
      {
        name: strings.GlobalLabels.All,
        code: "ALL",
        displayCode: null,
      },
      ...serviceOptions?.filter(
        (child) => child?.code !== ServiceEnum.ADD_BALANCE,
      ),
    ];
  }, [serviceOptions]);

  return {
    loading,
    dataForCharts,
    revenue,
    resultObject,
    config,
    serviceOptions,
    transactionReportData: mappedTransactionReportData,
    transactionReportLoading,
    productCache,
    getTransactionReport,
    transactionDetailReportData: mappedTransactionDetailReportData,
    transactionDetailReportLoading,
    getTransactionDetailReport,
    exportTransactionDetailReport,
    storeOptions,
    staffOptions,
    selectedStores,
    selectedStaffs,
    carrierList,
    partnerList,
    filterForm,
    setSelectedStores,
    setSelectedStaffs,
    staffLoading,
    service,
    setService,
    range,
    setRange,
    currency,
    setCurrency,
    currencyOptions,
    reportType,
    finalFilterOptions,
    selectedPartnerType,
    isStore,
    processedStatCards,
    periodLabels,
    processedTableData,
    processedRevenueData,
    revenueColumns,
    volumeColumns,
    handleExport,
    resetFilters,
    onFilterValuesChange,
    onGeneralFilterValuesChange,
    handlePartnerChange,
    handleStoreChange,
    handleStaffChange,
    getDataForReports,
  };
};

export default GeneralViewModel;
